import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Activity, 
  Database, 
  Server, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  Cpu,
  HardDrive,
  Zap,
  TrendingUp,
  Calendar,
  Mail,
  Cloud
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SystemHealthPage() {
  const [healthData, setHealthData] = useState(null);
  const [errors, setErrors] = useState([]);
  const [apiMetrics, setApiMetrics] = useState(null);
  const [dbStats, setDbStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchHealthData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/system/health`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setHealthData(data);
      }
    } catch (error) {
      console.error('Error fetching health data:', error);
    }
  }, []);

  const fetchErrors = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/system/errors?hours=24`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setErrors(data.errors || []);
      }
    } catch (error) {
      console.error('Error fetching errors:', error);
    }
  }, []);

  const fetchApiMetrics = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/system/api-metrics?minutes=60`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setApiMetrics(data);
      }
    } catch (error) {
      console.error('Error fetching API metrics:', error);
    }
  }, []);

  const fetchDbStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/system/database-stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDbStats(data);
      }
    } catch (error) {
      console.error('Error fetching DB stats:', error);
    }
  }, []);

  const refreshAll = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchHealthData(),
      fetchErrors(),
      fetchApiMetrics(),
      fetchDbStats()
    ]);
    setLastRefresh(new Date());
    setRefreshing(false);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await refreshAll();
      setLoading(false);
    };
    loadData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(refreshAll, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'online':
      case 'connected':
      case 'configured':
      case 'healthy':
        return 'bg-green-500';
      case 'offline':
      case 'error':
      case 'not_connected':
        return 'bg-red-500';
      case 'warning':
      case 'stopped':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'online': 'bg-green-100 text-green-800',
      'connected': 'bg-green-100 text-green-800',
      'configured': 'bg-green-100 text-green-800',
      'offline': 'bg-red-100 text-red-800',
      'error': 'bg-red-100 text-red-800',
      'not_connected': 'bg-gray-100 text-gray-800',
      'not_configured': 'bg-gray-100 text-gray-800',
      'stopped': 'bg-yellow-100 text-yellow-800',
      'unknown': 'bg-gray-100 text-gray-600'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-600';
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="system-health-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
        <span className="ml-2 text-gray-600">Caricamento dati sistema...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="system-health-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Health Dashboard</h1>
          <p className="text-gray-500">Monitoraggio in tempo reale del sistema ABITALO CRM</p>
        </div>
        <div className="flex items-center gap-4">
          {lastRefresh && (
            <span className="text-sm text-gray-500">
              Ultimo aggiornamento: {lastRefresh.toLocaleTimeString('it-IT')}
            </span>
          )}
          <Button 
            onClick={refreshAll} 
            disabled={refreshing}
            variant="outline"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Uptime */}
        <Card data-testid="uptime-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Uptime</p>
                <p className="text-xl font-bold text-gray-900">
                  {healthData?.uptime?.uptime_formatted || 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CPU */}
        <Card data-testid="cpu-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Cpu className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">CPU</p>
                <p className="text-xl font-bold text-gray-900">
                  {healthData?.system_resources?.cpu_percent?.toFixed(1) || 0}%
                </p>
              </div>
            </div>
            <Progress 
              value={healthData?.system_resources?.cpu_percent || 0} 
              className="mt-2 h-2"
            />
          </CardContent>
        </Card>

        {/* Memory */}
        <Card data-testid="memory-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Server className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">Memoria</p>
                <p className="text-xl font-bold text-gray-900">
                  {healthData?.system_resources?.memory?.percent?.toFixed(1) || 0}%
                </p>
              </div>
            </div>
            <Progress 
              value={healthData?.system_resources?.memory?.percent || 0} 
              className="mt-2 h-2"
            />
            <p className="text-xs text-gray-400 mt-1">
              {healthData?.system_resources?.memory?.used_gb || 0} / {healthData?.system_resources?.memory?.total_gb || 0} GB
            </p>
          </CardContent>
        </Card>

        {/* Disk */}
        <Card data-testid="disk-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <HardDrive className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">Disco</p>
                <p className="text-xl font-bold text-gray-900">
                  {healthData?.system_resources?.disk?.percent?.toFixed(1) || 0}%
                </p>
              </div>
            </div>
            <Progress 
              value={healthData?.system_resources?.disk?.percent || 0} 
              className="mt-2 h-2"
            />
            <p className="text-xs text-gray-400 mt-1">
              {healthData?.system_resources?.disk?.used_gb || 0} / {healthData?.system_resources?.disk?.total_gb || 0} GB
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="services" className="space-y-4">
        <TabsList>
          <TabsTrigger value="services">Servizi</TabsTrigger>
          <TabsTrigger value="database">Database</TabsTrigger>
          <TabsTrigger value="api">Performance API</TabsTrigger>
          <TabsTrigger value="errors">Errori</TabsTrigger>
        </TabsList>

        {/* Services Tab */}
        <TabsContent value="services" data-testid="services-tab">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Stato Servizi
              </CardTitle>
              <CardDescription>Monitoraggio dei servizi critici del sistema</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {healthData?.services?.map((service, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {service.name === 'MongoDB' && <Database className="w-5 h-5 text-green-600" />}
                      {service.name === 'APScheduler' && <Clock className="w-5 h-5 text-blue-600" />}
                      {service.name === 'SMTP' && <Mail className="w-5 h-5 text-purple-600" />}
                      {service.name === 'Google Calendar' && <Calendar className="w-5 h-5 text-red-600" />}
                      <div>
                        <p className="font-medium text-gray-900">{service.name}</p>
                        {service.jobs !== undefined && (
                          <p className="text-xs text-gray-500">{service.jobs} job attivi</p>
                        )}
                        {service.connected_users !== undefined && (
                          <p className="text-xs text-gray-500">{service.connected_users} utenti connessi</p>
                        )}
                        {service.host && (
                          <p className="text-xs text-gray-500">{service.host}</p>
                        )}
                      </div>
                    </div>
                    <Badge className={getStatusBadge(service.status)}>
                      {service.status === 'online' && <CheckCircle className="w-3 h-3 mr-1" />}
                      {service.status === 'offline' && <XCircle className="w-3 h-3 mr-1" />}
                      {service.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Database Tab */}
        <TabsContent value="database" data-testid="database-tab">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                Database Statistics
              </CardTitle>
              <CardDescription>
                {dbStats?.database_name} - {dbStats?.total_size_mb} MB totali
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{dbStats?.collections_count || 0}</p>
                  <p className="text-sm text-gray-600">Collections</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {(dbStats?.total_documents || 0).toLocaleString('it-IT')}
                  </p>
                  <p className="text-sm text-gray-600">Documenti Totali</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-600">{dbStats?.index_size_mb || 0} MB</p>
                  <p className="text-sm text-gray-600">Indici</p>
                </div>
              </div>

              <h4 className="font-medium text-gray-900 mb-3">Top Collections</h4>
              <div className="space-y-2">
                {dbStats?.collections?.slice(0, 10).map((coll, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{coll.name}</p>
                      <p className="text-xs text-gray-500">{coll.indexes || 0} indici</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        {(coll.documents || 0).toLocaleString('it-IT')} docs
                      </p>
                      <p className="text-xs text-gray-500">{coll.size_kb || 0} KB</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Performance Tab */}
        <TabsContent value="api" data-testid="api-tab">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Performance API
              </CardTitle>
              <CardDescription>
                Metriche degli ultimi 60 minuti - {apiMetrics?.total_requests || 0} richieste
              </CardDescription>
            </CardHeader>
            <CardContent>
              {apiMetrics?.total_requests > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {apiMetrics?.success_rate || 0}%
                      </p>
                      <p className="text-sm text-gray-600">Success Rate</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {apiMetrics?.avg_response_ms || 0} ms
                      </p>
                      <p className="text-sm text-gray-600">Avg Response</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-600">
                        {apiMetrics?.p95_response_ms || 'N/A'} ms
                      </p>
                      <p className="text-sm text-gray-600">P95 Response</p>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-orange-600">
                        {apiMetrics?.total_requests || 0}
                      </p>
                      <p className="text-sm text-gray-600">Total Requests</p>
                    </div>
                  </div>

                  {apiMetrics?.endpoints && Object.keys(apiMetrics.endpoints).length > 0 && (
                    <>
                      <h4 className="font-medium text-gray-900 mb-3">Top Endpoints</h4>
                      <div className="space-y-2">
                        {Object.entries(apiMetrics.endpoints).slice(0, 10).map(([endpoint, stats]) => (
                          <div 
                            key={endpoint}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded"
                          >
                            <div>
                              <p className="font-mono text-sm text-gray-900">{endpoint}</p>
                              <p className="text-xs text-gray-500">
                                {stats.count} richieste | {stats.errors} errori
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-gray-900">{stats.avg_time_ms} ms</p>
                              <p className="text-xs text-gray-500">avg</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Nessuna metrica API disponibile</p>
                  <p className="text-sm">Le metriche verranno raccolte automaticamente</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Errors Tab */}
        <TabsContent value="errors" data-testid="errors-tab">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Errori Recenti
              </CardTitle>
              <CardDescription>Ultimi errori nelle ultime 24 ore</CardDescription>
            </CardHeader>
            <CardContent>
              {errors.length > 0 ? (
                <div className="space-y-3">
                  {errors.map((error) => (
                    <div 
                      key={error.id || `${error.timestamp}-${error.action}`}
                      className="p-4 bg-red-50 border border-red-100 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-red-800">
                            {error.action || error.level || 'Error'}
                          </p>
                          <p className="text-sm text-red-600 mt-1">
                            {error.entity_type && `${error.entity_type}: ${error.entity_id}`}
                            {error.message && error.message}
                          </p>
                          {error.details && (
                            <pre className="text-xs text-gray-600 mt-2 bg-white p-2 rounded overflow-x-auto">
                              {JSON.stringify(error.details, null, 2)}
                            </pre>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">
                          {error.timestamp && new Date(error.timestamp).toLocaleString('it-IT')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-300" />
                  <p>Nessun errore nelle ultime 24 ore</p>
                  <p className="text-sm">Il sistema funziona correttamente</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
